/**
 * @file index.tsx
 *
 * @fileoverview Renders the pagination component.
 */

import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { Pager as PagerComponent } from '~/vcl/atoms/08-navigation/03-pager';
import PagerLink from '~/jobiqo/components/Pager/Link';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import { removeNextJsQueryParams } from '~/jobiqo/utils/routes';
import { ArrowLeftDoubleIcon } from '~/vcl/icons/ArrowLeftDouble';
import { ArrowLeftIcon } from '~/vcl/icons/ArrowLeft';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { ArrowRightIcon } from '~/vcl/icons/ArrowRight';
import { removeGeoLocationFromQueryString } from '~/jobiqo/components/Search/utils/searchHelpers';

export interface PagerProps {
  /**
   * Number of total items.
   */
  items: number;
  /**
   * Number of items per page.
   */
  itemsPerPage: number;
  /**
   * When going to a new page which filters to clear.
   */
  filtersToExclude?: string[];
  /**
   * An optional prop that will be used as the current querystring.
   * The parent component can manipulate the querstring if needed.
   */
  querystring?: any;
  /**
   * Skip geo location removal from querystring.
   * Used when the location key is used as a filter (e.g., job manager).
   */
  skipGeoLocationRemoval?: boolean;
}

export default function Pager({
  items,
  itemsPerPage,
  filtersToExclude,
  querystring: querystringProps,
  skipGeoLocationRemoval = false,
}: PagerProps) {
  if (items < itemsPerPage) {
    return null;
  }
  const intl = useIntl();
  const pages = Array.from(Array(Math.ceil(items / itemsPerPage)).keys());

  const {
    querystring: pathQuerystring,
    path,
  }: PathContextInterface = useContext(PathContext);

  // If the parent component defines the querystring we
  // give it an advantage over the path context querystring.
  const querystring = querystringProps ?? pathQuerystring;
  const currentPage = querystring.page ? parseInt(querystring.page, 10) : 1;

  // Remove the geo location from the querystring only if there is no geo_location key in the
  // url and skipGeoLocationRemoval is not set.
  const cleanQueryString =
    !skipGeoLocationRemoval && !path.includes('geo_location')
      ? removeGeoLocationFromQueryString(querystring)
      : querystring;

  const pagerLinkProps = {
    filtersToExclude,
    path: removeNextJsQueryParams(path),
    querystring: cleanQueryString,
  };

  return (
    <PagerComponent>
      {currentPage > 2 && (
        <li>
          <PagerLink pageNumber={1} {...pagerLinkProps}>
            <ArrowLeftDoubleIcon
              title={intl.formatMessage({
                id: 'pager_first',
                defaultMessage: 'First',
              })}
            />
          </PagerLink>
        </li>
      )}
      {currentPage > 1 && (
        <li>
          <PagerLink
            pageNumber={currentPage - 1}
            {...pagerLinkProps}
            data-testid="pager_previous"
          >
            <ArrowLeftIcon
              title={intl.formatMessage({
                id: 'pager_previous',
                defaultMessage: 'Previous',
              })}
            />
          </PagerLink>
        </li>
      )}
      {pages.length > 1 && (
        <li>
          <Spacing
            marginTop={0}
            marginBottom={0}
            marginRight={5}
            marginLeft={6}
          >
            {intl.formatNumber(currentPage)} / {intl.formatNumber(pages.length)}
          </Spacing>
        </li>
      )}
      {currentPage < pages.length && (
        <li>
          <PagerLink
            pageNumber={currentPage + 1}
            {...pagerLinkProps}
            data-testid="pager_next"
          >
            <ArrowRightIcon
              title={intl.formatMessage({
                id: 'pager_next',
                defaultMessage: 'Next',
              })}
            />
          </PagerLink>
        </li>
      )}
    </PagerComponent>
  );
}
